import React, { useEffect } from "react";

import { GoogleOAuthProvider } from "@react-oauth/google";

import clsx from "clsx";

import { ToastContainer } from "react-toastify";

import { useQuery } from "@apollo/client";
import {
  BrowserRouter,
  Switch,
  Redirect,
  Route as RegularRoute,
} from "react-router-dom";

import Route from "../components/Route";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";

import { AuthProvider } from "../context/Auth";
import { ApolloProvider } from "../context/Apollo";
import { ClientProvider, useCurrentClient } from "../context/Client";
import { SidebarProvider, useSidebarState } from "../context/Sidebar";

import { QUERY_AUTH } from "../config/graphql/query";

import * as Page from "../pages";
import { CurrentClientRoute } from "../pages/Clients";

import useHasRole from "../lib/hooks/useHasRole";
import { useCenterParcs } from "../lib/hooks/useCenterParcs";

const PublicRouter = React.memo(() => (
  <div className="d-flex" id="wrapper">
    <div className="container-fluid">
      <Switch>
        <Route.Public exact path="/login">
          <Page.Login />
        </Route.Public>
        <Route.Public exact path="/forgot-password">
          <Page.ForgotPassword />
        </Route.Public>
        <Route.Public exact path="/verify-2fa">
          <Page.Verify2fa />
        </Route.Public>
        <RegularRoute exact path="/reset-password">
          <Page.ResetPassword />
        </RegularRoute>
        <RegularRoute exact path="/verify-email">
          <Page.EmailVerification />
        </RegularRoute>
        <Route.Public path="/access-portal" allowLoggedIn>
          <Page.AccessPortal />
        </Route.Public>
      </Switch>
    </div>
  </div>
));

const Wrapper = React.memo<{ children?: React.ReactNode }>((props) => {
  const { children } = props;

  const { visible } = useSidebarState();

  return (
    <div
      id="wrapper"
      className={clsx("d-flex", {
        toggled: visible,
      })}
    >
      {children}
    </div>
  );
});

const PrivateRouter = React.memo(() => {
  const { data } = useQuery<{ clientId: string }>(QUERY_AUTH);

  const clientId = data?.clientId;

  const isActivityRole = useHasRole(["ACTIVITY", "ACTIVITY_CHECKED_IN"]);
  const isPickUpRole = useHasRole(["PICK_UP"]);
  const isServiceEmployeeRole = useHasRole(["SERVICE_EMPLOYEE"]);

  const client = useCurrentClient();

  let redirectUrl = "/entity";

  if (isActivityRole) {
    redirectUrl = "/activity";
  }

  if (isPickUpRole) {
    redirectUrl = "/courier-deliveries";
  }

  if (isServiceEmployeeRole) {
    redirectUrl = "/reception-contact";
  }

  if (client?.code === "jungheinrich") {
    redirectUrl = "/company";
  }

  if (client?.code === "center-parcs") {
    redirectUrl = "/minew-devices";
  }

  const { isCenterParcsClient } = useCenterParcs();

  useEffect(() => {
    if (isCenterParcsClient) {
      document.body.classList.add("centerparcs-theme");
    } else {
      document.body.classList.remove("centerparcs-theme");
    }

    return () => {
      document.body.classList.remove("centerparcs-theme");
    };
  }, [isCenterParcsClient]);

  return (
    <SidebarProvider>
      <Wrapper>
        <Sidebar />
        <div
          key={clientId}
          id="page-content-wrapper"
          className="d-flex flex-column"
        >
          <Navbar />

          <Switch>
            <Route.Private exact path="/">
              <Redirect to={redirectUrl} />
              {/* <Page.Dashboard /> */}
            </Route.Private>
            <Route.Private path="/entity">
              <Page.Entities />
            </Route.Private>
            <Route.Private path="/company">
              <Page.Companies />
            </Route.Private>
            <Route.Private path="/deceased">
              <Page.Deceased />
            </Route.Private>
            <Route.Private path="/devices">
              <Page.Devices />
            </Route.Private>
            <Route.Private path="/msi-devices">
              <Page.MsiDevice />
            </Route.Private>
            <Route.Role
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT", "SERVICE_EMPLOYEE"]}
              path="/profile"
            >
              <Page.Profile />
            </Route.Role>

            <Route.Role
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT", "PICK_UP"]}
              path="/courier-deliveries"
              blockInForeignContext
            >
              <Page.CourierDeliveries />
            </Route.Role>

            <Route.Role
              roles={["ADMIN", "CLIENT_ADMIN", "SERVICE_EMPLOYEE"]}
              path="/reception-contact"
            >
              <Page.ReceptionContact />
            </Route.Role>

            <Route.Role
              path="/client"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
            >
              <CurrentClientRoute />
            </Route.Role>
            <Route.Role
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
              path="/users"
            >
              <Page.Users />
            </Route.Role>

            <Route.Role
              path="/employees"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
              blockInForeignContext
            >
              <Page.Employees />
            </Route.Role>

            <Route.Role
              path="/visitors"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
              blockInForeignContext
            >
              <Page.Visitors />
            </Route.Role>

            <Route.Role
              path="/students"
              roles={["ADMIN", "CLIENT_ADMIN"]}
              blockInForeignContext
            >
              <Page.Students />
            </Route.Role>

            <Route.Role
              path="/access-labels"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
              blockInForeignContext
            >
              <Page.AccessLabels />
            </Route.Role>

            <Route.Role
              path="/meetings"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
              blockInForeignContext
            >
              <Page.Meetings />
            </Route.Role>

            <Route.Role
              path="/activity"
              roles={[
                "ADMIN",
                "CLIENT_ADMIN",
                "CLIENT",
                "ACTIVITY",
                "ACTIVITY_CHECKED_IN",
              ]}
              blockInForeignContext
            >
              <Page.Activity />
            </Route.Role>

            <Route.Role
              path="/digital-receptions"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
            >
              <Page.DigitalReceptions />
            </Route.Role>

            <Route.Role
              path="/nameboards"
              roles={["ADMIN", "CLIENT_ADMIN", "CLIENT"]}
            >
              <Page.Nameboards />
            </Route.Role>

            <Route.Private path="/wayfinding">
              <Page.Wayfinding />
            </Route.Private>

            <Route.Private path="/minew-devices">
              <Page.MinewDevices />
            </Route.Private>

            <Route.Private path="/support">
              <Page.Support />
            </Route.Private>

            <Route.Private path="/floorInfo">
              <Page.FloorInfos />
            </Route.Private>
            <Route.Private path="/postbox">
              <Page.Postboxes />
            </Route.Private>

            <Route.Role path="/toro/visitors" roles={["ADMIN"]}>
              <Page.VisitorLog />
            </Route.Role>
            <Route.Role path="/toro/meetings" roles={["ADMIN"]}>
              <Page.ToroMeetings />
            </Route.Role>

            <Route.Role path="/clients" roles={["ADMIN"]}>
              <Page.Clients />
            </Route.Role>
            <Route.Role path="/visionects" roles={["ADMIN"]}>
              <Page.Visionects />
            </Route.Role>
            <Route.Role path="/minews" roles={["ADMIN"]}>
              <Page.Minews />
            </Route.Role>
            <Route.Role path="/logs" roles={["ADMIN"]}>
              <Page.Logs />
            </Route.Role>
            <Route.Role path="/errorLogs" roles={["ADMIN"]}>
              <Page.ErrorsLog />
            </Route.Role>
            <Route.Role path="/userActivityLogs" roles={["ADMIN"]}>
              <Page.UserActivityLogs />
            </Route.Role>
            <Route.Role path="/twilio-usage" roles={["ADMIN"]}>
              <Page.TwilioUsage />
            </Route.Role>
            <Route.Role path="/twilio-costs" roles={["ADMIN"]}>
              <Page.TwilioCosts />
            </Route.Role>
            <Route.Role path="/assets" roles={["ADMIN"]}>
              <Page.Assets />
            </Route.Role>
            <Redirect to="/" />
          </Switch>
        </div>
      </Wrapper>
    </SidebarProvider>
  );
});

const Root: React.FunctionComponent = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID!;
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <ApolloProvider>
          <AuthProvider>
            <Switch>
              <RegularRoute
                exact
                path={[
                  "/login",
                  "/forgot-password",
                  "/reset-password",
                  "/verify-email",
                  "/verify-2fa",
                  "/access-portal",
                ]}
              >
                <PublicRouter />
              </RegularRoute>
              <Route.Private path="/">
                <ClientProvider>
                  <PrivateRouter />
                </ClientProvider>
              </Route.Private>
            </Switch>
          </AuthProvider>
        </ApolloProvider>
      </BrowserRouter>
      <ToastContainer position="bottom-right" />
    </GoogleOAuthProvider>
  );
};

export default Root;
